import { useBunja } from 'bunja/react';
import { useStore } from 'jotai';
import { useTranslations } from 'next-intl';
import { useCallback, useRef } from 'react';

import { OrderTypeEnum } from '@flipster/apis/types';
import { deletePendingPositionApi, putAmendOrderPriceApi } from '@flipster/apis/v2/trade';
import { accountBunja } from '@flipster/r230522/original/account';
import {
  ConditionalOrderTypeEnum,
  ordersBunja,
  type Order,
  type PendingPosition,
} from '@flipster/r230522/original/order';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { getErrorMessage } from '@flipster/utils/error';
import Num, { BigNumber } from '@flipster/utils/num';
import segment, { ChartEvent } from '@flipster/utils/segment';

import { orderAttributionAtom } from '../shared/order-attribution';
import { rawInstrumentsKeyBySymbolAtom } from '../shared/snapshot/state';
import { SymbolScope } from '../shared/symbol/bunja';
import { buildTradeRequestBase } from '../shared/trade-actions/base-params';
import { replaceTpslTriggerPrice } from '../shared/trade-actions/tpsl/state';
import { tradingSessionCheckBunja } from '../shared/trading-suspension/state';

class DragValidationError extends Error {}

export function useOrderDragMutation({ symbol }: { symbol: string }) {
  const store = useStore();
  const transAssets = useTranslations('Assets.r250401');
  const transPosition = useTranslations('Position.r250401');
  const transTrade = useTranslations('Trade.r260408');
  const { ordersAtom, pendingPositionsAtom } = useBunja(ordersBunja);
  const { v2AccountAtom } = useBunja(accountBunja);
  // 차트에 표시되는 주문은 차트 심볼의 주문이므로, off-hours 가드는 차트 심볼 스코프로 바인딩한다.
  const { guardOffHoursAlert } = useBunja(tradingSessionCheckBunja, [SymbolScope.bind(symbol)]);

  const requestCounterRef = useRef<Record<string, number>>({});

  const findPendingOrder = useCallback(
    (orderId: string): PendingPosition | undefined => {
      const pendingPositions = store.get(pendingPositionsAtom);
      if (!pendingPositions) return undefined;
      return Object.values(pendingPositions)
        .flat()
        .find((o) => o.orderId === orderId);
    },
    [store, pendingPositionsAtom],
  );

  /**
   * Find a TP/SL order by orderId in the raw ordersAtom.
   * Keys in ordersAtom are WebSocket-assigned (not necessarily orderId),
   * so we search by value.
   */
  const findOrderById = useCallback(
    (orderId: string): { order: Order; symbol: string } | undefined => {
      const allOrders = store.get(ordersAtom);
      if (!allOrders) return undefined;
      const order = Object.values(allOrders).find((o) => o.orderId === orderId);
      if (!order) return undefined;
      return { order, symbol: order.symbol };
    },
    [store, ordersAtom],
  );

  const onDragComplete = useCallback(
    async (orderId: string, newPrice: number) => {
      const counter = (requestCounterRef.current[orderId] ?? 0) + 1;
      requestCounterRef.current[orderId] = counter;

      const attribution = store.get(orderAttributionAtom);
      const tradeMode = store.get(v2AccountAtom)?.tradeMode || 'MultiplePositions';

      // Check if this is a TP/SL order (exists in ordersAtom with TP/SL conditional type)
      const tpslResult = findOrderById(orderId);
      const isTpSl =
        tpslResult &&
        (tpslResult.order.conditionalOrderType === ConditionalOrderTypeEnum.PositionTp ||
          tpslResult.order.conditionalOrderType === ConditionalOrderTypeEnum.PositionSl);

      // Resolve order once to avoid race condition (WS updates may remove it between lookups)
      const pendingOrder = !isTpSl ? findPendingOrder(orderId) : undefined;
      const orderSymbol = isTpSl ? tpslResult.symbol : pendingOrder?.symbol;
      const instruments = store.get(rawInstrumentsKeyBySymbolAtom);
      const tickSize = Num(instruments?.[orderSymbol ?? '']?.tickSize || 0);
      const rawPrice = Num(newPrice);
      const alignedPrice = tickSize.gt(0)
        ? rawPrice.div(tickSize).integerValue(BigNumber.ROUND_HALF_DOWN).times(tickSize)
        : rawPrice;
      const newPriceStr = alignedPrice.toString();

      try {
        if (isTpSl) {
          const { order, symbol } = tpslResult;
          const orderType = order.conditionalOrderType === ConditionalOrderTypeEnum.PositionTp ? 'TP' : 'SL';
          await replaceTpslTriggerPrice(order, newPriceStr, tradeMode, attribution, symbol);

          if (requestCounterRef.current[orderId] !== counter) return;
          snackbar.success({ message: transPosition('Position_update_toast') });
          segment.track(ChartEvent.ChartEventOrderDrag, {
            order_type: orderType,
            old_price: order.triggerPrice,
            new_price: newPriceStr,
          });
        } else {
          if (!pendingOrder) return;

          // Tradable off-hours: 주문 Modify 전 알림 (ERD-240 §8.2 — reduce-only는 exempt).
          // 취소 시 DragValidationError로 전파해 (추가 토스트 없이) 차트 라인을 원래 가격으로 snap-back시킨다.
          if (!pendingOrder.isReduceOnly && !(await guardOffHoursAlert('orderModify'))) {
            throw new DragValidationError();
          }

          const isLimit = pendingOrder.orderType === OrderTypeEnum['Limit'];
          const orderType = isLimit ? 'Limit' : 'Trigger';
          const oldPrice = isLimit ? pendingOrder.price ?? '' : pendingOrder.triggerPrice;

          // Validate min notional: price × size >= notionalMinOrderAmount
          if (isLimit) {
            const instrument = instruments?.[pendingOrder.symbol];
            const minNotional = Num(instrument?.notionalMinOrderAmount || 0);
            const notional = alignedPrice.times(Num(pendingOrder.qty).abs());
            if (minNotional.gt(0) && notional.lt(minNotional)) {
              snackbar.error({ message: transTrade('Order_size_less_than_min_limit_toast') });
              throw new DragValidationError();
            }
          }

          await putAmendOrderPriceApi(pendingOrder.symbol, orderId, {
            ...buildTradeRequestBase(attribution),
            ...(isLimit ? { price: newPriceStr } : { triggerPrice: newPriceStr, side: pendingOrder.side }),
          });

          if (requestCounterRef.current[orderId] !== counter) return;
          snackbar.success({ message: transTrade('Order_updated_toast') });
          segment.track(ChartEvent.ChartEventOrderDrag, {
            order_type: orderType,
            old_price: oldPrice,
            new_price: newPriceStr,
          });
        }
      } catch (error) {
        if (requestCounterRef.current[orderId] !== counter) return;
        if (!(error instanceof DragValidationError)) {
          snackbar.error({ message: getErrorMessage(error) });
        }
        throw error;
      }
    },
    [findPendingOrder, findOrderById, store, v2AccountAtom, transPosition, transTrade, guardOffHoursAlert],
  );

  const onCancelClick = useCallback(
    async (orderId: string) => {
      const attribution = store.get(orderAttributionAtom);

      try {
        const tpslResult = findOrderById(orderId);
        const isTpSl =
          tpslResult &&
          (tpslResult.order.conditionalOrderType === ConditionalOrderTypeEnum.PositionTp ||
            tpslResult.order.conditionalOrderType === ConditionalOrderTypeEnum.PositionSl);

        if (isTpSl) {
          const { order, symbol } = tpslResult;
          await deletePendingPositionApi(symbol, order.orderId, buildTradeRequestBase(attribution));
          snackbar.success({ message: transAssets('Order_canceled_message') });
          segment.track(ChartEvent.ChartEventOrderCancel, {
            order_type: order.conditionalOrderType === ConditionalOrderTypeEnum.PositionTp ? 'TP' : 'SL',
            price: order.triggerPrice,
          });
        } else {
          const order = findPendingOrder(orderId);
          if (!order) return;
          const isLimit = order.orderType === OrderTypeEnum['Limit'];
          await deletePendingPositionApi(order.symbol, orderId, buildTradeRequestBase(attribution));
          snackbar.success({ message: transAssets('Order_canceled_message') });
          segment.track(ChartEvent.ChartEventOrderCancel, {
            order_type: isLimit ? 'Limit' : 'Trigger',
            price: isLimit ? order.price ?? '' : order.triggerPrice,
          });
        }
      } catch (error) {
        snackbar.error({ message: getErrorMessage(error) });
        throw error;
      }
    },
    [findPendingOrder, findOrderById, store, transAssets],
  );

  return { onDragComplete, onCancelClick };
}
